/**
 * Super-admin tenant detail page — `/superadmin/tenants/:id`.
 *
 * Cross-tenant deep-dive. Backed by `GET /v1/superadmin/societies/:id/detail`,
 * which combines profile + lifetime _count + last-7-day usage + storage_bytes
 * + last_admin_login in a single round-trip.
 *
 * Feature flags edit inline via `PATCH /v1/superadmin/societies/:id/features`;
 * recent activity panel re-uses `GET /v1/superadmin/audit-log?society_id=…`.
 */
import { useMemo, useState } from 'react';
import { useParams, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building,
  ShieldAlert,
  Activity,
  HardDrive,
  Clock3,
  Camera,
  ScanLine,
  Users,
  Megaphone,
  AlertTriangle,
  Receipt,
  CreditCard,
  FileText,
  Sparkles
} from 'lucide-react';
import { get, getErrorMessage, patch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDateTime, formatRelative } from '@/lib/format';
import { UsageTrendCard } from '@/components/superadmin/UsageTrendCard';

type TenantStatus = 'trial' | 'active' | 'suspended' | 'cancelled';
type SosScope = 'guards_admins' | 'plus_block' | 'plus_society';

interface SocietyProfile {
  id: string;
  name: string;
  jurisdiction_tag: string;
  country: string;
  city: string | null;
  state: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  timezone: string;
  visitor_photo_retention_days: number;
  feature_face_recog_enabled: boolean;
  feature_anpr_enabled: boolean;
  feature_gate_photo_mandatory: boolean;
  feature_family_can_approve: boolean;
  sos_broadcast_scope: SosScope;
  tenant_status: TenantStatus;
  suspended_at: string | null;
  suspended_reason: string | null;
  trial_expires_at: string | null;
  // Catalog-driven: any active PlanTier code (free/lite/pro/enterprise or custom).
  plan: string;
  plan_status: 'active' | 'past_due' | 'cancelled';
  plan_started_at: string | null;
  plan_renews_at: string | null;
  plan_price_inr_per_month: number | null;
  plan_seats_units: number | null;
  created_at: string;
  updated_at: string;
  _count: {
    units: number;
    residents: number;
    guards: number;
    admins: number;
    visitors: number;
    notices: number;
    complaints: number;
    maintenance_bills: number;
    community_posts: number;
    documents: number;
    amenities: number;
    vehicles: number;
  };
}

interface DetailResponse {
  profile: SocietyProfile;
  last_admin_login: {
    at: string;
    actor_user_id: string | null;
    actor_role: string;
  } | null;
  storage_bytes: number;
  usage_7d: {
    visitors_checked_in: number;
    notices_published: number;
    complaints_raised: number;
    bills_generated: number;
    payments_captured: number;
    documents_uploaded: number;
  };
}

interface AuditEntry {
  id: string;
  society_id: string | null;
  society_name: string | null;
  actor_user_id: string | null;
  actor_role: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip: string | null;
  user_agent: string | null;
  at: string;
}

interface AuditListResponse {
  items: AuditEntry[];
  next_cursor: string | null;
}

const STATUS_BADGE: Record<TenantStatus, { label: string; variant: 'warn' | 'ok' | 'alert' | 'muted' }> = {
  trial: { label: 'Trial', variant: 'warn' },
  active: { label: 'Active', variant: 'ok' },
  suspended: { label: 'Suspended', variant: 'alert' },
  cancelled: { label: 'Cancelled', variant: 'muted' }
};

const SOS_SCOPE_LABEL: Record<SosScope, string> = {
  guards_admins: 'Guards + admins only',
  plus_block: 'Add residents in same block',
  plus_society: 'Add every resident in society'
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// Inline saved/saving indicator next to each toggle.
type SaveState = 'idle' | 'saving' | 'saved' | 'error';
function SaveIndicator({ state }: { state: SaveState }): JSX.Element {
  if (state === 'saving') return <span className="text-vt-12 text-vt-text-muted">Saving…</span>;
  if (state === 'saved') return <span className="text-vt-12 text-vt-safe">Saved</span>;
  if (state === 'error') return <span className="text-vt-12 text-vt-alert">Error</span>;
  return <span className="text-vt-12 text-vt-text-soft">&nbsp;</span>;
}

interface FeaturesPatch {
  feature_face_recog_enabled?: boolean;
  feature_anpr_enabled?: boolean;
  feature_gate_photo_mandatory?: boolean;
  feature_family_can_approve?: boolean;
  sos_broadcast_scope?: SosScope;
}

export function SuperAdminTenantDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const queryClient = useQueryClient();
  const [saveStates, setSaveStates] = useState<Record<keyof FeaturesPatch, SaveState>>({
    feature_face_recog_enabled: 'idle',
    feature_anpr_enabled: 'idle',
    feature_gate_photo_mandatory: 'idle',
    feature_family_can_approve: 'idle',
    sos_broadcast_scope: 'idle'
  });

  const detail = useQuery({
    queryKey: ['superadmin', 'tenant-detail', id],
    queryFn: () => get<DetailResponse>(`/superadmin/societies/${id}/detail`)
  });

  const recent = useQuery({
    queryKey: ['superadmin', 'tenant-recent-audit', id],
    queryFn: () =>
      get<AuditListResponse>(`/superadmin/audit-log?society_id=${id}&limit=10`),
    refetchOnWindowFocus: false
  });

  const updateFeatures = useMutation({
    mutationFn: (body: FeaturesPatch) => patch<SocietyProfile>(`/superadmin/societies/${id}/features`, body),
    onMutate: (body) => {
      const next = { ...saveStates };
      for (const k of Object.keys(body) as Array<keyof FeaturesPatch>) {
        next[k] = 'saving';
      }
      setSaveStates(next);
    },
    onSuccess: (_data, body) => {
      const next = { ...saveStates };
      for (const k of Object.keys(body) as Array<keyof FeaturesPatch>) {
        next[k] = 'saved';
      }
      setSaveStates(next);
      // Reset the saved indicator after 1.6 s so it doesn't sit forever.
      setTimeout(() => {
        setSaveStates((prev) => {
          const copy = { ...prev };
          for (const k of Object.keys(body) as Array<keyof FeaturesPatch>) {
            if (copy[k] === 'saved') copy[k] = 'idle';
          }
          return copy;
        });
      }, 1600);
      toast.success('Feature flag updated');
      void queryClient.invalidateQueries({ queryKey: ['superadmin', 'tenant-detail', id] });
    },
    onError: (err, body) => {
      const next = { ...saveStates };
      for (const k of Object.keys(body) as Array<keyof FeaturesPatch>) {
        next[k] = 'error';
      }
      setSaveStates(next);
      toast.error(getErrorMessage(err));
    }
  });

  const profile = detail.data?.profile;
  const usage = detail.data?.usage_7d;
  const counts = profile?._count;
  const storageBytes = detail.data?.storage_bytes ?? 0;
  const lastAdminLogin = detail.data?.last_admin_login ?? null;

  // Lifetime counts — typed list keeps the grid table-data-driven.
  const lifetimeCounts = useMemo(() => {
    if (!counts) return [];
    return [
      { key: 'units', label: 'Units', value: counts.units },
      { key: 'residents', label: 'Residents', value: counts.residents },
      { key: 'guards', label: 'Guards', value: counts.guards },
      { key: 'admins', label: 'Admins', value: counts.admins },
      { key: 'visitors', label: 'Visitors', value: counts.visitors },
      { key: 'notices', label: 'Notices', value: counts.notices },
      { key: 'complaints', label: 'Complaints', value: counts.complaints },
      { key: 'maintenance_bills', label: 'Bills', value: counts.maintenance_bills },
      { key: 'community_posts', label: 'Community posts', value: counts.community_posts },
      { key: 'documents', label: 'Documents', value: counts.documents },
      { key: 'amenities', label: 'Amenities', value: counts.amenities },
      { key: 'vehicles', label: 'Vehicles', value: counts.vehicles }
    ];
  }, [counts]);

  const usageRows = useMemo(() => {
    if (!usage) return [];
    return [
      { key: 'visitors', label: 'Visitors checked in', value: usage.visitors_checked_in, icon: Users },
      { key: 'notices', label: 'Notices published', value: usage.notices_published, icon: Megaphone },
      { key: 'complaints', label: 'Complaints raised', value: usage.complaints_raised, icon: AlertTriangle },
      { key: 'bills', label: 'Bills generated', value: usage.bills_generated, icon: Receipt },
      { key: 'payments', label: 'Payments captured', value: usage.payments_captured, icon: CreditCard },
      { key: 'documents', label: 'Documents uploaded', value: usage.documents_uploaded, icon: FileText }
    ];
  }, [usage]);

  // Max usage value used to size the inline progress bars.
  const usageMax = useMemo(() => {
    if (!usage) return 1;
    return Math.max(
      1,
      usage.visitors_checked_in,
      usage.notices_published,
      usage.complaints_raised,
      usage.bills_generated,
      usage.payments_captured,
      usage.documents_uploaded
    );
  }, [usage]);

  if (detail.isLoading) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-6">
        <Skeleton className="h-12 w-1/2" />
        <div className="grid grid-cols-1 gap-vt-4 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (detail.isError || !profile) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-vt-6 pt-vt-6">
        <EmptyState
          icon={<Building className="h-6 w-6" />}
          title="Could not load tenant"
          description={getErrorMessage(detail.error)}
          action={
            <Button variant="secondary" onClick={() => void detail.refetch()}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  const statusInfo = STATUS_BADGE[profile.tenant_status] ?? STATUS_BADGE.active;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-6">
      {/* Header */}
      <div className="flex flex-col gap-vt-2">
        <Link
          to="/superadmin/dashboard"
          className="inline-flex w-fit items-center gap-1 text-vt-13 text-vt-primary hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to tenants
        </Link>
        <div className="flex flex-wrap items-center gap-vt-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-13 font-semibold text-vt-primary">
            {profile.name
              .split(' ')
              .map((w) => w[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-vt-24 font-semibold tracking-[-0.01em] text-vt-text">
              {profile.name}
            </h1>
            <div className="mt-0.5 flex items-center gap-vt-2 text-vt-12 text-vt-text-muted">
              <span className="font-mono">{profile.id.slice(0, 8)}…</span>
              <span>·</span>
              <span>{profile.timezone}</span>
            </div>
          </div>
          <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
          {profile.tenant_status === 'suspended' && profile.suspended_reason ? (
            <Badge variant="muted" className="max-w-[300px] truncate" title={profile.suspended_reason}>
              <ShieldAlert className="mr-1 h-3 w-3" /> {profile.suspended_reason}
            </Badge>
          ) : null}
        </div>
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-1 gap-vt-4 lg:grid-cols-3">
        {/* Profile */}
        <Card>
          <CardContent className="space-y-vt-3 p-vt-4">
            <h2 className="text-vt-14 font-semibold text-vt-text">Profile</h2>
            <dl className="space-y-vt-2 text-vt-13">
              <Row label="Jurisdiction">
                <Badge variant="muted" className="font-mono">
                  {profile.jurisdiction_tag.replace('_', '-')}
                </Badge>
              </Row>
              <Row label="Country">{profile.country}</Row>
              <Row label="City / State">
                {profile.city ?? '—'}
                {profile.state ? `, ${profile.state}` : ''}
              </Row>
              <Row label="Postal code">{profile.postal_code ?? '—'}</Row>
              <Row label="Timezone">{profile.timezone}</Row>
              <Row label="Provisioned">{formatDateTime(profile.created_at)}</Row>
            </dl>
          </CardContent>
        </Card>

        {/* Lifecycle */}
        <Card>
          <CardContent className="space-y-vt-3 p-vt-4">
            <h2 className="text-vt-14 font-semibold text-vt-text">Lifecycle</h2>
            <dl className="space-y-vt-2 text-vt-13">
              <Row label="Status">
                <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
              </Row>
              <Row label="Trial expires">
                {profile.trial_expires_at ? formatDateTime(profile.trial_expires_at) : '—'}
              </Row>
              <Row label="Suspended at">
                {profile.suspended_at ? formatDateTime(profile.suspended_at) : '—'}
              </Row>
              <Row label="Suspended reason">{profile.suspended_reason ?? '—'}</Row>
              <Row label="Visitor photo retention">
                {profile.visitor_photo_retention_days} days
              </Row>
            </dl>
          </CardContent>
        </Card>

        {/* Subscription */}
        <SubscriptionCard
          profile={profile}
          onSaved={() => detail.refetch()}
        />

        {/* Feature flags */}
        <Card>
          <CardContent className="space-y-vt-3 p-vt-4">
            <h2 className="text-vt-14 font-semibold text-vt-text">Feature flags</h2>
            <div className="space-y-vt-2 text-vt-13">
              <FeatureToggle
                icon={<Camera className="h-4 w-4" />}
                label="Face recognition"
                description="Use face vectors at the gate"
                checked={profile.feature_face_recog_enabled}
                disabled={updateFeatures.isPending}
                onChange={(v) =>
                  updateFeatures.mutate({ feature_face_recog_enabled: v })
                }
                state={saveStates.feature_face_recog_enabled}
              />
              <FeatureToggle
                icon={<ScanLine className="h-4 w-4" />}
                label="ANPR (number plate)"
                description="Auto-scan vehicle plates at the gate"
                checked={profile.feature_anpr_enabled}
                disabled={updateFeatures.isPending}
                onChange={(v) =>
                  updateFeatures.mutate({ feature_anpr_enabled: v })
                }
                state={saveStates.feature_anpr_enabled}
              />
              <FeatureToggle
                icon={<Camera className="h-4 w-4" />}
                label="Gate photo mandatory"
                description="Guard must capture visitor photo on entry"
                checked={profile.feature_gate_photo_mandatory}
                disabled={updateFeatures.isPending}
                onChange={(v) =>
                  updateFeatures.mutate({ feature_gate_photo_mandatory: v })
                }
                state={saveStates.feature_gate_photo_mandatory}
              />
              <FeatureToggle
                icon={<Users className="h-4 w-4" />}
                label="Family can approve"
                description="Adult family members can approve own-flat visitors"
                checked={profile.feature_family_can_approve}
                disabled={updateFeatures.isPending}
                onChange={(v) =>
                  updateFeatures.mutate({ feature_family_can_approve: v })
                }
                state={saveStates.feature_family_can_approve}
              />
              <div className="flex flex-col gap-1 rounded-vt-sm border border-vt-border bg-vt-surface-2/40 p-vt-2">
                <div className="flex items-start justify-between gap-vt-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-vt-text">
                      <Sparkles className="h-4 w-4 shrink-0" />
                      <span className="font-medium">SOS broadcast scope</span>
                    </div>
                    <p className="text-vt-12 text-vt-text-muted">
                      Who receives a panic alert?
                    </p>
                  </div>
                  <SaveIndicator state={saveStates.sos_broadcast_scope} />
                </div>
                <Select
                  value={profile.sos_broadcast_scope}
                  onChange={(e) =>
                    updateFeatures.mutate({
                      sos_broadcast_scope: e.target.value as SosScope
                    })
                  }
                  disabled={updateFeatures.isPending}
                >
                  <option value="guards_admins">{SOS_SCOPE_LABEL.guards_admins}</option>
                  <option value="plus_block">{SOS_SCOPE_LABEL.plus_block}</option>
                  <option value="plus_society">{SOS_SCOPE_LABEL.plus_society}</option>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage (last 7 days) */}
        <Card className="lg:col-span-2">
          <CardContent className="space-y-vt-3 p-vt-4">
            <div className="flex items-center justify-between">
              <h2 className="text-vt-14 font-semibold text-vt-text">Usage · last 7 days</h2>
              <Activity className="h-4 w-4 text-vt-text-muted" />
            </div>
            <div className="space-y-vt-2">
              {usageRows.map((u) => {
                const Icon = u.icon;
                const pct = Math.round((u.value / usageMax) * 100);
                return (
                  <div key={u.key} className="flex items-center gap-vt-3 text-vt-13">
                    <Icon className="h-4 w-4 shrink-0 text-vt-text-muted" />
                    <span className="w-40 shrink-0 text-vt-text-muted">{u.label}</span>
                    <div className="relative h-2 flex-1 overflow-hidden rounded-vt-pill bg-vt-surface-2">
                      <div
                        className="absolute inset-y-0 left-0 rounded-vt-pill bg-vt-primary"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-12 shrink-0 text-right font-mono text-vt-text">{u.value}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Per-tenant trend (30/90 day deep view). Sits next to the
            instantaneous 7-day usage card — `Usage 7d` stays the at-a-glance
            summary; this card is the historic series the operator drills into
            when they suspect a regression. */}
        <div className="lg:col-span-3">
          <UsageTrendCard
            title="Usage trends"
            endpoint={`/superadmin/societies/${id}/usage`}
            queryKey={['superadmin', 'tenant-usage', id]}
          />
        </div>

        {/* Storage + last admin login mini-card */}
        <Card>
          <CardContent className="space-y-vt-4 p-vt-4">
            <div>
              <div className="flex items-center justify-between">
                <h2 className="text-vt-14 font-semibold text-vt-text">Storage</h2>
                <HardDrive className="h-4 w-4 text-vt-text-muted" />
              </div>
              <div className="mt-vt-2 text-vt-24 font-semibold text-vt-text">
                {formatBytes(storageBytes)}
              </div>
              <p className="mt-1 text-vt-12 text-vt-text-muted">
                Sum of all document uploads
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <h2 className="text-vt-14 font-semibold text-vt-text">Last admin login</h2>
                <Clock3 className="h-4 w-4 text-vt-text-muted" />
              </div>
              <div className="mt-vt-2 text-vt-text">
                {lastAdminLogin ? formatRelative(lastAdminLogin.at) : '—'}
              </div>
              {lastAdminLogin ? (
                <p className="mt-1 text-vt-12 text-vt-text-muted">
                  {formatDateTime(lastAdminLogin.at)} · {lastAdminLogin.actor_role}
                </p>
              ) : (
                <p className="mt-1 text-vt-12 text-vt-text-muted">No admin has signed in yet.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Lifetime counts */}
        <Card className="lg:col-span-3">
          <CardContent className="space-y-vt-3 p-vt-4">
            <h2 className="text-vt-14 font-semibold text-vt-text">Lifetime counts</h2>
            <div className="grid grid-cols-2 gap-vt-3 sm:grid-cols-3 lg:grid-cols-6">
              {lifetimeCounts.map((c) => (
                <div
                  key={c.key}
                  className="rounded-vt-md border border-vt-border bg-vt-surface-2/40 p-vt-3"
                >
                  <div className="text-vt-12 text-vt-text-muted">{c.label}</div>
                  <div className="mt-1 font-mono text-vt-18 font-semibold text-vt-text">{c.value}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent activity */}
        <Card className="lg:col-span-3">
          <CardContent className="space-y-vt-3 p-vt-4">
            <div className="flex items-center justify-between">
              <h2 className="text-vt-14 font-semibold text-vt-text">Recent activity</h2>
              <Link
                to="/superadmin/audit"
                className="text-vt-12 text-vt-primary hover:underline"
              >
                View full audit log →
              </Link>
            </div>
            {recent.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full" />
                ))}
              </div>
            ) : recent.data && recent.data.items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Entity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent.data.items.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap text-vt-text-muted">
                        {formatDateTime(e.at)}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center rounded-vt-sm bg-vt-surface-2 px-2 py-0.5 font-mono text-vt-12 text-vt-text">
                          {e.action}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="muted">{e.actor_role}</Badge>
                      </TableCell>
                      <TableCell className="text-vt-text-muted">
                        {e.entity_type}
                        {e.entity_id ? (
                          <span className="ml-1 font-mono text-vt-11 text-vt-text-soft">
                            {e.entity_id.slice(0, 8)}…
                          </span>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-vt-13 text-vt-text-muted">No recent activity for this tenant.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Small label / value row used in profile + lifecycle cards.
// Catalog-safe badge variant — unknown custom tier codes fall back to 'muted'.
function getPlanBadgeVariant(code: string): 'muted' | 'info' | 'ok' | 'warn' {
  const KNOWN: Record<string, 'muted' | 'info' | 'ok' | 'warn'> = {
    free: 'muted',
    lite: 'info',
    pro: 'ok',
    enterprise: 'warn'
  };
  return KNOWN[code] ?? 'muted';
}

// Plan-tier catalog row — shared shape across superadmin UIs.
interface PlanTierCatalogRow {
  code: string;
  label: string;
  description: string | null;
  price_inr_per_month: number;
  seats_cap_units: number | null;
  sort_order: number;
  is_active: boolean;
  tenants_count?: number;
}
interface PlanTierListResponse {
  items: PlanTierCatalogRow[];
}

const LEGACY_PLAN_OPTIONS: ReadonlyArray<{
  code: string;
  label: string;
  price_inr_per_month: number;
  seats_cap_units: number | null;
}> = [
  { code: 'free', label: 'Free', price_inr_per_month: 0, seats_cap_units: 25 },
  { code: 'lite', label: 'Lite', price_inr_per_month: 499, seats_cap_units: 100 },
  { code: 'pro', label: 'Pro', price_inr_per_month: 1999, seats_cap_units: 500 },
  { code: 'enterprise', label: 'Enterprise', price_inr_per_month: 0, seats_cap_units: null }
];

function formatTierOption(t: {
  label: string;
  price_inr_per_month: number;
  seats_cap_units: number | null;
}): string {
  const priceLabel = t.price_inr_per_month > 0 ? ` — ₹${t.price_inr_per_month.toLocaleString('en-IN')}/mo` : '';
  const seatsLabel = t.seats_cap_units !== null ? ` (${t.seats_cap_units} units)` : ' (unlimited)';
  return `${t.label}${priceLabel}${seatsLabel}`;
}

const PLAN_STATUS_BADGE: Record<SocietyProfile['plan_status'], 'ok' | 'alert' | 'muted'> = {
  active: 'ok',
  past_due: 'alert',
  cancelled: 'muted'
};

interface PlanPatch {
  plan: SocietyProfile['plan'];
  plan_status?: SocietyProfile['plan_status'];
  plan_started_at?: string | null;
  plan_renews_at?: string | null;
  plan_price_inr_per_month?: number | null;
  plan_seats_units?: number | null;
}

function SubscriptionCard({
  profile,
  onSaved
}: {
  profile: SocietyProfile;
  onSaved: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [plan, setPlan] = useState<SocietyProfile['plan']>(profile.plan);
  const [planStatus, setPlanStatus] = useState<SocietyProfile['plan_status']>(profile.plan_status);
  const [price, setPrice] = useState<string>(
    profile.plan_price_inr_per_month === null ? '' : String(profile.plan_price_inr_per_month)
  );
  const [seats, setSeats] = useState<string>(
    profile.plan_seats_units === null ? '' : String(profile.plan_seats_units)
  );
  const [renewsAt, setRenewsAt] = useState<string>(profile.plan_renews_at?.slice(0, 10) ?? '');
  // Track manual overrides so changing the tier doesn't clobber explicit entries.
  const [priceOverridden, setPriceOverridden] = useState(false);
  const [seatsOverridden, setSeatsOverridden] = useState(false);

  // Catalog query — shares cache key with superadmin-plans + dashboard dialog.
  const tiersQuery = useQuery({
    queryKey: ['superadmin', 'plan-tiers'],
    queryFn: () => get<PlanTierListResponse>('/superadmin/plan-tiers'),
    enabled: editing
  });
  const tiers = useMemo(() => {
    const items = tiersQuery.data?.items;
    if (!items || items.length === 0) {
      if (tiersQuery.isError) return LEGACY_PLAN_OPTIONS;
      return [];
    }
    return items
      .filter((t) => t.is_active)
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code));
  }, [tiersQuery.data, tiersQuery.isError]);

  const onPlanChange = (next: string): void => {
    setPlan(next);
    const tier = tiers.find((t) => t.code === next);
    if (!tier) return;
    if (!priceOverridden) setPrice(String(tier.price_inr_per_month));
    if (!seatsOverridden) setSeats(tier.seats_cap_units === null ? '' : String(tier.seats_cap_units));
  };

  const mutation = useMutation({
    mutationFn: (body: PlanPatch) =>
      patch<SocietyProfile>(`/superadmin/societies/${profile.id}/plan`, body),
    onSuccess: () => {
      toast.success('Subscription updated');
      setEditing(false);
      onSaved();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const submit = (): void => {
    const body: PlanPatch = {
      plan,
      plan_status: planStatus,
      plan_price_inr_per_month: price.trim() === '' ? null : Number.parseInt(price, 10),
      plan_seats_units: seats.trim() === '' ? null : Number.parseInt(seats, 10),
      plan_renews_at: renewsAt.trim() === '' ? null : new Date(renewsAt).toISOString()
    };
    mutation.mutate(body);
  };

  return (
    <Card>
      <CardContent className="space-y-vt-3 p-vt-4">
        <div className="flex items-center justify-between">
          <h2 className="text-vt-14 font-semibold text-vt-text">Subscription</h2>
          {!editing ? (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              Change plan
            </Button>
          ) : null}
        </div>
        {!editing ? (
          <div className="space-y-vt-2 text-vt-13">
            <Row label="Plan">
              <span className="flex items-center gap-vt-2">
                <Badge variant={getPlanBadgeVariant(profile.plan)} className="capitalize">
                  {profile.plan}
                </Badge>
                <Badge variant={PLAN_STATUS_BADGE[profile.plan_status]} className="capitalize">
                  {profile.plan_status.replace('_', ' ')}
                </Badge>
              </span>
            </Row>
            <Row label="Price">
              {profile.plan_price_inr_per_month === null
                ? 'Contact us'
                : profile.plan_price_inr_per_month === 0
                  ? 'Free'
                  : `₹${profile.plan_price_inr_per_month.toLocaleString('en-IN')}/month`}
            </Row>
            <Row label="Seats">
              {profile.plan_seats_units === null
                ? 'Unlimited'
                : `${profile.plan_seats_units} units`}
            </Row>
            <Row label="Started">
              {profile.plan_started_at ? new Date(profile.plan_started_at).toLocaleDateString('en-IN') : '—'}
            </Row>
            <Row label="Renews">
              {profile.plan_renews_at ? new Date(profile.plan_renews_at).toLocaleDateString('en-IN') : '—'}
            </Row>
          </div>
        ) : (
          <div className="space-y-vt-3 text-vt-13">
            <label className="flex flex-col gap-vt-1">
              <span className="text-vt-text-soft">Plan</span>
              <Select
                value={plan}
                onChange={(e) => onPlanChange(e.target.value)}
                disabled={tiersQuery.isLoading}
              >
                {tiers.length === 0 && !tiersQuery.isLoading ? (
                  <option value={profile.plan} disabled>
                    Add tiers in /superadmin/plans
                  </option>
                ) : null}
                {tiers.find((t) => t.code === plan) ? null : (
                  <option value={plan}>{plan} (current)</option>
                )}
                {tiers.map((t) => (
                  <option key={t.code} value={t.code}>
                    {formatTierOption(t)}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-vt-1">
              <span className="text-vt-text-soft">Status</span>
              <Select
                value={planStatus}
                onChange={(e) => setPlanStatus(e.target.value as SocietyProfile['plan_status'])}
              >
                <option value="active">Active</option>
                <option value="past_due">Past due</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </label>
            <label className="flex flex-col gap-vt-1">
              <span className="text-vt-text-soft">Price (₹/month)</span>
              <input
                type="number"
                min="0"
                value={price}
                placeholder="Leave blank for plan default"
                onChange={(e) => {
                  setPrice(e.target.value);
                  setPriceOverridden(true);
                }}
                className="rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13"
              />
            </label>
            <label className="flex flex-col gap-vt-1">
              <span className="text-vt-text-soft">Unit seats</span>
              <input
                type="number"
                min="1"
                value={seats}
                placeholder="Blank = plan default"
                onChange={(e) => {
                  setSeats(e.target.value);
                  setSeatsOverridden(true);
                }}
                className="rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13"
              />
            </label>
            <label className="flex flex-col gap-vt-1">
              <span className="text-vt-text-soft">Renews on</span>
              <input
                type="date"
                value={renewsAt}
                onChange={(e) => setRenewsAt(e.target.value)}
                className="rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13"
              />
            </label>
            <div className="flex justify-end gap-vt-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={mutation.isPending}>
                Cancel
              </Button>
              <Button size="sm" onClick={submit} loading={mutation.isPending} loadingText="Saving…">
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-vt-3">
      <dt className="shrink-0 text-vt-text-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right text-vt-text">{children}</dd>
    </div>
  );
}

// One feature flag toggle row. Native checkbox keeps the component dep-free.
function FeatureToggle({
  icon,
  label,
  description,
  checked,
  onChange,
  disabled,
  state
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  state: SaveState;
}): JSX.Element {
  return (
    <label
      className={
        'flex items-start gap-vt-3 rounded-vt-sm border border-vt-border bg-vt-surface-2/40 p-vt-2 transition-colors hover:border-vt-border-strong ' +
        (disabled ? 'opacity-70' : '')
      }
    >
      <span className="mt-0.5 text-vt-text-muted">{icon}</span>
      <span className="flex-1">
        <span className="block text-vt-text">{label}</span>
        <span className="block text-vt-12 text-vt-text-muted">{description}</span>
      </span>
      <span className="flex flex-col items-end gap-1">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 cursor-pointer accent-[color:var(--vt-primary)]"
          aria-label={label}
        />
        <SaveIndicator state={state} />
      </span>
    </label>
  );
}
